import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AppLayout } from "@/components/AppLayout";
import { motion } from "framer-motion";
import { FileCheck, AlertTriangle, FolderCog, Home } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, completed: 0, error: 0 });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [logsRes, configsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/processing-logs?limit=10`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/configs`, { withCredentials: true })
      ]);
      setLogs(logsRes.data);
      setConfigs(configsRes.data);
      const total = logsRes.data.length;
      const completed = logsRes.data.filter(l => l.status === 'completed').length;
      const error = logsRes.data.filter(l => l.status === 'error').length;
      setStats({ total, completed, error });
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const statCards = [
    { label: 'Total Processed', value: stats.total, color: 'sage', icon: FileCheck },
    { label: 'Completed', value: stats.completed, color: 'green', icon: FileCheck },
    { label: 'Errors', value: stats.error, color: 'red', icon: AlertTriangle },
    { label: 'Households', value: configs.length, color: 'amber', icon: Home },
  ];

  const colorMap = {
    sage: { bg: 'bg-sage-50', border: 'border-l-sage-500', text: 'text-sage-700', icon: 'text-sage-500' },
    green: { bg: 'bg-green-50', border: 'border-l-green-500', text: 'text-green-700', icon: 'text-green-500' },
    red: { bg: 'bg-red-50', border: 'border-l-red-500', text: 'text-red-700', icon: 'text-red-500' },
    amber: { bg: 'bg-amber-50', border: 'border-l-amber-500', text: 'text-amber-700', icon: 'text-amber-500' },
  };

  return (
    <AppLayout user={user} activeNav="/dashboard">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="mb-8">
          <h2 className="text-2xl font-heading font-extrabold text-stone-900">Overview</h2>
          <p className="text-stone-500 text-sm mt-1">Your invoice processing at a glance</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statCards.map((stat, i) => {
            const c = colorMap[stat.color];
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                <Card className={`border-l-4 ${c.border} bg-white/90 backdrop-blur-sm`}>
                  <CardContent className="pt-5 pb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold text-stone-400 uppercase tracking-wide">{stat.label}</p>
                        <p className={`text-3xl font-heading font-extrabold ${c.text} mt-1`} data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>{stat.value}</p>
                      </div>
                      <div className={`w-10 h-10 ${c.bg} rounded-xl flex items-center justify-center`}>
                        <stat.icon className={`w-5 h-5 ${c.icon}`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        <Card className="bg-white/90 backdrop-blur-sm border border-stone-200/60">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg">Recent Activity</CardTitle>
              <Button variant="outline" size="sm" onClick={() => navigate('/households')} className="text-sage-600 border-sage-200 hover:bg-sage-50 rounded-full">
                View Households
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-sage-500 border-t-transparent mx-auto"></div>
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-12">
                <FolderCog className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500">No processing logs yet</p>
                <p className="text-sm text-stone-400 mt-1">Set up a configuration and process your first invoice</p>
                <Button onClick={() => navigate('/configurations')} className="mt-4 bg-sage-500 hover:bg-sage-600 rounded-xl" data-testid="setup-first-config">
                  Get Started
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-stone-100">
                      <th className="px-4 py-3 text-left text-[10px] font-bold text-stone-400 uppercase tracking-wider">File</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold text-stone-400 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold text-stone-400 uppercase tracking-wider">Vendor</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold text-stone-400 uppercase tracking-wider">Amount</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold text-stone-400 uppercase tracking-wider">Processed</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-50">
                    {logs.map((log) => (
                      <tr key={log.log_id} data-testid={`log-row-${log.log_id}`} className="hover:bg-stone-50/50 transition">
                        <td className="px-4 py-3 text-sm text-stone-700 max-w-[200px] truncate">{log.file_name}</td>
                        <td className="px-4 py-3">
                          <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-full ${
                            log.status === 'completed' ? 'bg-green-100 text-green-700' :
                            log.status === 'error' ? 'bg-red-100 text-red-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {log.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-stone-500">{log.extracted_data?.vendor_name || '-'}</td>
                        <td className="px-4 py-3 text-sm font-medium text-stone-700">{log.extracted_data?.amount ? `₹${log.extracted_data.amount}` : '-'}</td>
                        <td className="px-4 py-3 text-xs text-stone-400">{formatDate(log.processed_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </AppLayout>
  );
};

export default Dashboard;
